/**
 * La correction du prof, écrite à la main sur la feuille de brouillon,
 * sous la réponse : ✓ ou ✗ entouré, une appréciation, les points, puis le
 * détail (erreur commise, correction) écrit à la main. Remplace la popup de
 * résultat dans le mode « sur la feuille ». L'encre suit le score (`teacherInk`) :
 * vert de 80 à 100 %, orange de 50 à 79 %, rouge sous 50 %.
 *
 * `FinalTeacherGrade` : la note finale de la copie (« 14/20 »), en bas de la feuille.
 * `TeacherScoreStamp` : le score (« 100 % ») qui tombe sur la feuille comme un tampon.
 */

'use client';

import './paper.css';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { MarkdownMath } from '../content/markdown-math';
import {
  formatGrade,
  scorePercent,
  teacherAppreciation,
  teacherBonuses,
  teacherGain,
  teacherInk,
  teacherRemark,
  type TeacherNoteInput,
} from './teacher-note-text';

export interface TeacherNoteProps extends TeacherNoteInput {
  /** l'erreur commise (choix faux), Markdown + LaTeX */
  errorFeedbackMd?: string | null;
  /** la correction de la question, Markdown + LaTeX */
  explanationMd?: string | null;
  /** remplace l'appréciation (ex. « Indices trouvés ! ») */
  remark?: string;
  /** sous la note (ex. HORA gagnés sur la SKILLCARD, rang dans un live) */
  extra?: ReactNode;
  /** question déjà passée : note plus discrète, sans annonce */
  past?: boolean;
  className?: string;
}

export function TeacherNote(props: TeacherNoteProps) {
  const { wasCorrect, errorFeedbackMd, explanationMd, extra, past = false, className } = props;
  const remark = props.remark ?? teacherRemark(props);
  const gain = teacherGain(props);
  const bonuses = wasCorrect ? teacherBonuses(props) : null;
  const error = errorFeedbackMd?.trim();
  // une bonne réponse n'a pas besoin de la correction écrite en entier
  const explanation = explanationMd?.trim();
  return (
    <div
      className={cn('pp-teacher-note', past && 'pp-teacher-note-past', className)}
      data-testid="teacher-note"
      data-verdict={wasCorrect ? 'good' : 'bad'}
      data-ink={teacherInk(wasCorrect ? 100 : 0)}
      role={past ? undefined : 'status'}
      aria-live={past ? undefined : 'polite'}
    >
      <div className="pp-teacher-head">
        <span className="pp-teacher-mark" aria-label={wasCorrect ? 'Juste' : 'Faux'}>
          {wasCorrect ? '✓' : '✗'}
        </span>
        <span className="pp-teacher-remark">{remark}</span>
        {gain && <span className="pp-teacher-gain">{gain}</span>}
      </div>
      {bonuses && <p className="pp-teacher-small">{bonuses}</p>}
      {!wasCorrect && error && (
        <div className="pp-teacher-detail">
          <span className="pp-teacher-label">Ton erreur :</span>
          <MarkdownMath>{error}</MarkdownMath>
        </div>
      )}
      {explanation && (!wasCorrect || !past) && (
        <div className="pp-teacher-detail">
          <span className="pp-teacher-label">{wasCorrect ? 'Pourquoi :' : 'Correction :'}</span>
          <MarkdownMath>{explanation}</MarkdownMath>
        </div>
      )}
      {extra && <div className="pp-teacher-extra">{extra}</div>}
    </div>
  );
}

/** La note de la copie entière, en bas de la feuille : « 14/20 » entouré + appréciation. */
export function FinalTeacherGrade({
  correct,
  total,
  grade,
  remark,
  children,
  className,
}: {
  correct: number;
  total: number;
  /** note sur 20 (sinon calculée d'après correct / total) */
  grade: number;
  remark?: string;
  /** le détail écrit sous la note (XP, HORA, rang…), à la main et à l'encre du prof */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn('pp-final-grade', className)}
      data-testid="final-teacher-grade"
      data-ink={teacherInk(grade * 5)}
    >
      <div className="pp-final-grade-row">
        <span
          className="pp-final-grade-value"
          aria-label={`Note : ${formatGrade(grade)} sur 20`}
          data-testid="final-grade-value"
        >
          {formatGrade(grade)}
          <span className="pp-final-grade-on" aria-hidden>
            /20
          </span>
        </span>
        <span className="pp-final-grade-remark">{remark ?? teacherAppreciation(grade)}</span>
      </div>
      <p className="pp-teacher-small">
        {correct} bonne{correct > 1 ? 's' : ''} réponse{correct > 1 ? 's' : ''} sur {total}
      </p>
      {children && (
        <div className="pp-final-grade-detail" data-testid="final-grade-detail">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Le score de la copie qui tombe sur la feuille comme un tampon (« 100 % » entouré,
 * à l'encre du prof) + l'appréciation. Sert à la fin d'une démo.
 */
export function TeacherScoreStamp({
  correct,
  total,
  remark,
  className,
}: {
  correct: number;
  total: number;
  remark?: string;
  className?: string;
}) {
  const percent = scorePercent(correct, total);
  return (
    <div
      className={cn('pp-final-grade pp-score-stamp', className)}
      data-testid="teacher-score-stamp"
      data-ink={teacherInk(percent)}
      role="status"
      aria-live="polite"
    >
      <div className="pp-final-grade-row">
        <span
          className="pp-final-grade-value pp-stamp"
          aria-label={`Score : ${percent} %`}
          data-testid="score-stamp-value"
        >
          {percent}
          <span className="pp-final-grade-on" aria-hidden>
            %
          </span>
        </span>
        <span className="pp-final-grade-remark">{remark ?? teacherAppreciation(percent / 5)}</span>
      </div>
    </div>
  );
}
